import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, FileText, ShoppingCart, Truck, CreditCard, Shield, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CGV = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Retour à l'accueil</span>
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Conditions Générales de Vente</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Préambule */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-orange-500" />
                <span>Préambule</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <p>
                  Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre la société OUTILMATPRO, 
                  SAS au capital de 5 000 €, immatriculée au RCS de Béziers sous le numéro 797 802 279, 
                  dont le siège social est situé 7 Rue du Général de Monsabert, 34500 Béziers, France, 
                  ci-après dénommée "le Vendeur" et toute personne physique ou morale, ci-après dénommée "l'Acheteur" ou "le Client".
                </p>
                <p>
                  Ces CGV s'appliquent à toutes les ventes de produits et services proposés par OUTILMATPRO, 
                  que ce soit en magasin, par correspondance, par téléphone ou via notre site internet.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 1 - Objet */}
          <Card>
            <CardHeader>
              <CardTitle>Article 1 - Objet</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <p>
                  OUTILMATPRO est spécialisée dans la vente de disques diamantés et d'outillage professionnel 
                  destinés aux professionnels du bâtiment, notamment les carreleurs et maçons.
                </p>
                <p>
                  Les présentes CGV ont pour objet de définir les conditions dans lesquelles OUTILMATPRO 
                  fournit ses produits et services aux clients professionnels et particuliers.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 2 - Commandes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ShoppingCart className="h-5 w-5 text-orange-500" />
                <span>Article 2 - Commandes</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <h4>2.1 Passation de commande</h4>
                <p>
                  Les commandes peuvent être passées :
                </p>
                <ul>
                  <li>Par téléphone au +33 6 61 01 84 89</li>
                  <li>Par email à contact@outilmatpro.fr</li>
                  <li>Via notre site internet</li>
                  <li>Directement dans nos locaux</li>
                </ul>
                
                <h4>2.2 Confirmation de commande</h4>
                <p>
                  Toute commande fait l'objet d'une confirmation écrite (email ou courrier) précisant :
                </p>
                <ul>
                  <li>Les références et quantités des produits commandés</li>
                  <li>Les prix unitaires et le montant total</li>
                  <li>Les conditions de livraison</li>
                  <li>Les modalités de paiement</li>
                </ul>
                
                <h4>2.3 Acceptation de commande</h4>
                <p>
                  OUTILMATPRO se réserve le droit d'accepter ou de refuser toute commande, 
                  notamment en cas de quantités anormalement élevées ou de problème concernant le client.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 3 - Prix */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CreditCard className="h-5 w-5 text-orange-500" />
                <span>Article 3 - Prix et modalités de paiement</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <h4>3.1 Prix</h4>
                <p>
                  Les prix sont exprimés en euros, hors taxes pour les professionnels assujettis à la TVA, 
                  et toutes taxes comprises pour les particuliers. La TVA applicable est de 20%.
                </p>
                <p>
                  Les prix peuvent être modifiés à tout moment mais les produits seront facturés 
                  sur la base des tarifs en vigueur au moment de l'enregistrement des commandes.
                </p>
                
                <h4>3.2 Modalités de paiement</h4>
                <p>Le paiement peut s'effectuer :</p>
                <ul>
                  <li>Par virement bancaire</li>
                  <li>Par chèque à l'ordre d'OUTILMATPRO</li>
                  <li>Par carte bancaire (pour les achats en ligne ou sur place)</li>
                  <li>En espèces (dans la limite légale)</li>
                </ul>
                
                <h4>3.3 Délais de paiement</h4>
                <p>
                  Pour les professionnels : paiement à 30 jours net, sauf conditions particulières convenues.
                  Pour les particuliers : paiement comptant à la commande.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 4 - Livraison */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Truck className="h-5 w-5 text-orange-500" />
                <span>Article 4 - Livraison</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <h4>4.1 Zones de livraison</h4>
                <p>
                  OUTILMATPRO livre en France métropolitaine et dans les DOM-TOM. 
                  Pour les livraisons à l'étranger, nous consulter.
                </p>
                
                <h4>4.2 Délais de livraison</h4>
                <p>
                  Les délais de livraison sont donnés à titre indicatif :
                </p>
                <ul>
                  <li>Produits en stock : 2 à 5 jours ouvrés</li>
                  <li>Produits sur commande : 7 à 15 jours ouvrés</li>
                  <li>Livraison express possible (nous consulter)</li>
                </ul>
                
                <h4>4.3 Frais de livraison</h4>
                <p>
                  Les frais de livraison sont calculés selon le poids, le volume et la destination. 
                  Ils sont communiqués avant validation de la commande.
                </p>
                <p>
                  Livraison gratuite pour les commandes supérieures à 150€ HT en France métropolitaine.
                </p>
                
                <h4>4.4 Réception des marchandises</h4>
                <p>
                  L'acheteur doit vérifier l'état des marchandises à la réception et signaler 
                  toute anomalie dans les 48 heures suivant la livraison.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 5 - Garanties */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-orange-500" />
                <span>Article 5 - Garanties</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <h4>5.1 Garantie légale</h4>
                <p>
                  Tous nos produits bénéficient de la garantie légale de conformité et de la garantie contre les vices cachés, 
                  dans les conditions prévues par le Code de la consommation.
                </p>
                
                <h4>5.2 Garantie commerciale</h4>
                <p>
                  OUTILMATPRO offre une garantie commerciale sur ses produits :
                </p>
                <ul>
                  <li>Disques diamantés Elite Precision : garantie 2 ans</li>
                  <li>Disques diamantés ElitePro : garantie 1 an</li>
                  <li>Outillage électroportatif : selon garantie constructeur</li>
                  <li>Outillage à main : garantie à vie (hors usure normale)</li>
                </ul>
                
                <h4>5.3 Conditions de garantie</h4>
                <p>
                  La garantie s'applique en cas de défaut de fabrication ou de vice de matière, 
                  à l'exclusion de l'usure normale, de la mauvaise utilisation ou des dommages accidentels.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 6 - Retours et échanges */}
          <Card>
            <CardHeader>
              <CardTitle>Article 6 - Retours et échanges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <h4>6.1 Droit de rétractation (particuliers)</h4>
                <p>
                  Conformément au Code de la consommation, les particuliers disposent d'un délai de 14 jours 
                  pour exercer leur droit de rétractation, sans avoir à justifier de motifs ni à payer de pénalités.
                </p>
                
                <h4>6.2 Conditions de retour</h4>
                <p>
                  Les produits doivent être retournés dans leur emballage d'origine, 
                  en parfait état de revente, accompagnés de la facture d'achat.
                </p>
                
                <h4>6.3 Frais de retour</h4>
                <p>
                  Les frais de retour sont à la charge du client, sauf en cas de produit défectueux 
                  ou d'erreur de notre part.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 7 - Responsabilité */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                <span>Article 7 - Limitation de responsabilité</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <p>
                  La responsabilité d'OUTILMATPRO ne peut être engagée qu'en cas de faute prouvée. 
                  En tout état de cause, cette responsabilité est limitée au montant de la commande.
                </p>
                <p>
                  OUTILMATPRO ne saurait être tenue responsable des dommages indirects tels que 
                  perte d'exploitation, perte de clientèle, trouble commercial quelconque.
                </p>
                <p>
                  Il appartient au client de vérifier l'adéquation des produits commandés à l'usage qu'il souhaite en faire.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 8 - Force majeure */}
          <Card>
            <CardHeader>
              <CardTitle>Article 8 - Force majeure</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <p>
                  OUTILMATPRO ne saurait être tenue responsable de l'inexécution de ses obligations 
                  en cas de force majeure ou de circonstances indépendantes de sa volonté.
                </p>
                <p>
                  Sont considérés comme cas de force majeure : les catastrophes naturelles, 
                  les conflits sociaux, les épidémies, les décisions gouvernementales, 
                  les pannes d'équipement, les ruptures d'approvisionnement, etc.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 9 - Protection des données */}
          <Card>
            <CardHeader>
              <CardTitle>Article 9 - Protection des données personnelles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <p>
                  OUTILMATPRO s'engage à respecter la réglementation en vigueur en matière de protection des données personnelles, 
                  notamment le Règlement Général sur la Protection des Données (RGPD).
                </p>
                <p>
                  Les données collectées sont nécessaires au traitement de votre commande et à la gestion de la relation client. 
                  Elles ne sont pas transmises à des tiers sans votre consentement.
                </p>
                <p>
                  Vous disposez d'un droit d'accès, de rectification, de suppression et d'opposition 
                  concernant vos données personnelles. Pour exercer ces droits : contact@outilmatpro.fr
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 10 - Droit applicable */}
          <Card>
            <CardHeader>
              <CardTitle>Article 10 - Droit applicable et juridiction</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-gray max-w-none">
                <p>
                  Les présentes CGV sont soumises au droit français. 
                  En cas de litige, les tribunaux de Béziers seront seuls compétents.
                </p>
                <p>
                  Avant tout recours contentieux, les parties s'efforceront de résoudre leur différend à l'amiable. 
                  En cas d'échec, le litige sera soumis aux tribunaux compétents.
                </p>
                <p>
                  Pour les litiges de consommation, le client peut recourir à la médiation de la consommation 
                  ou saisir la juridiction de son domicile.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Contact et informations */}
          <Card className="bg-orange-50 border-orange-200">
            <CardHeader>
              <CardTitle className="text-orange-800">Contact et informations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-orange-700">
                <p className="font-semibold mb-2">OUTILMATPRO</p>
                <p>7 Rue du Général de Monsabert</p>
                <p>34500 Béziers, France</p>
                <p className="mt-2">Téléphone : +33 6 61 01 84 89</p>
                <p>Email : contact@outilmatpro.fr</p>
                <p className="mt-2 text-sm">
                  SIREN : 797 802 279 | SIRET : 797 802 279 00019<br/>
                  SAS au capital de 5 000 € | RCS Béziers
                </p>
                <p className="mt-4 text-sm font-medium">
                  CGV mises à jour le {new Date().toLocaleDateString('fr-FR')}
                </p>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default CGV;